import { Component, Injectable } from "@angular/core";
import { IACHPaymentTypeModel, IACHProcessPaymentModel, IACHProcessPaymentResultModel, ICreditCardPaymentTypeModel, ICreditCardProcessPaymentModel, ICreditCardProcessPaymentResult, IPaymentModel, PaymentModel } from "@models";
import { GlobalsService } from "../services.module";
import { PayTraceService } from "./paytrace.service";
import { USAePayService } from "./usaepay.service";
import { ZiftService } from "./zift.service";
import { BamboraService } from "./bambora.service";
import { NorthSIService } from "./northsi.service";



export interface IPaymentProcessorService {
    getNewCreditCardPaymentType(customerId: number): Promise<ICreditCardPaymentTypeModel>;
    getCreditCardPaymentType(creditCardPaymentTypeId: number): Promise<ICreditCardPaymentTypeModel>;
    getCreditCardPaymentTypeForCustomer(customerId: number): Promise<ICreditCardPaymentTypeModel[]>;
    getACHPaymentInfoForCustomer(customerId: number): Promise<IACHPaymentTypeModel[]>;
    getACHPaymentType(achPaymentTypeId: number): Promise<IACHPaymentTypeModel>;
    addCreditCardPaymentType(creditCardPaymentTypeModel: ICreditCardPaymentTypeModel): Promise<ICreditCardPaymentTypeModel>;
    updateCreditCardPaymentType(creditCardPaymentTypeModel: ICreditCardPaymentTypeModel): Promise<ICreditCardPaymentTypeModel>;
    deleteCreditCardPaymentType(creditCardPaymentTypeModel: ICreditCardPaymentTypeModel): Promise<ICreditCardPaymentTypeModel>;
    deleteACHPaymentType(achPaymentTypeModel: IACHPaymentTypeModel): Promise<IACHPaymentTypeModel>;
    processPrePayment(processCreditCardPaymentModel: ICreditCardProcessPaymentModel): Promise<ICreditCardProcessPaymentResult>;
    processPayment(processCreditCardPaymentModel: ICreditCardProcessPaymentModel): Promise<ICreditCardProcessPaymentResult>;
    voidPayment(paymentModel: IPaymentModel): Promise<IPaymentModel>;
    refundPayment(paymentModel: IPaymentModel, amount: number): Promise<IPaymentModel>;
    processACHPayment(processACHPaymentModel: IACHProcessPaymentModel): Promise<IACHProcessPaymentResultModel>;
    addACHPaymentType(achPaymentTypeModel: IACHPaymentTypeModel): Promise<IACHPaymentTypeModel>;
    updateACHPaymentType(achPaymentTypeModel: IACHPaymentTypeModel): Promise<IACHPaymentTypeModel>;

}



@Injectable()
export class PaymentProcessorService {
    private internalPaymentProcessorService: IPaymentProcessorService;
    private internalACHService: IPaymentProcessorService;
    constructor(
        private usaePayService: USAePayService,
        private bamboraService: BamboraService,
        private payTraceService: PayTraceService,
        private ziftService: ZiftService,
        private northSIService: NorthSIService) {
        const company = GlobalsService.company;
        if (company) {
            if (company.useBambora)
                this.internalPaymentProcessorService = this.bamboraService;
            else if (company.useCCPayTrace)
                this.internalPaymentProcessorService = this.payTraceService;
            else if (company.useCCUSAePay)
                this.internalPaymentProcessorService = this.usaePayService;
            else if (company.useCCZift)
                this.internalPaymentProcessorService = this.ziftService;

            if (company.useBambora)
                this.internalACHService = this.bamboraService;
            else if (company.useACHPayTrace)
                this.internalACHService = this.payTraceService;
            else if (company.useACHUSAePay)
                this.internalACHService = this.usaePayService;
            else if (company.useACHZift)
                this.internalACHService = this.ziftService;
        }
    }


    async getNewCreditCardPaymentType(customerId: number): Promise<ICreditCardPaymentTypeModel> {
        return this.internalPaymentProcessorService.getNewCreditCardPaymentType(customerId);
    }

    async getCreditCardPaymentType(creditCardPaymentTypeId: number): Promise<ICreditCardPaymentTypeModel> {
        return this.internalPaymentProcessorService.getCreditCardPaymentType(creditCardPaymentTypeId);
    }

    async getCreditCardPaymentTypeForCustomer(customerId: number): Promise<ICreditCardPaymentTypeModel[]> {
        return this.internalPaymentProcessorService.getCreditCardPaymentTypeForCustomer(customerId);
    }

    async getACHPaymentTypeForCustomer(customerId: number): Promise<IACHPaymentTypeModel[]> {
        return this.internalACHService.getACHPaymentInfoForCustomer(customerId);
    }

    async getACHPaymentType(achPaymentTypeId: number): Promise<IACHPaymentTypeModel> {
        return this.internalACHService.getACHPaymentType(achPaymentTypeId);
    }

    async addCreditCardPaymentType(creditCardPaymentTypeModel: ICreditCardPaymentTypeModel): Promise<ICreditCardPaymentTypeModel> {
        return this.internalPaymentProcessorService.addCreditCardPaymentType(creditCardPaymentTypeModel);
    }

    async updateCreditCardPaymentType(creditCardPaymentTypeModel: ICreditCardPaymentTypeModel): Promise<ICreditCardPaymentTypeModel> {
        return this.internalPaymentProcessorService.updateCreditCardPaymentType(creditCardPaymentTypeModel);
    }

    async addACHPaymentType(achPaymentTypeModel: IACHPaymentTypeModel): Promise<IACHPaymentTypeModel> {
        return this.internalACHService.addACHPaymentType(achPaymentTypeModel);
    }

    async updateACHPaymentType(achPaymentTypeModel: IACHPaymentTypeModel): Promise<IACHPaymentTypeModel> {
        return this.internalACHService.updateACHPaymentType(achPaymentTypeModel);
    }

    async deleteCreditCardPaymentType(creditCardPaymentTypeModel: ICreditCardPaymentTypeModel): Promise<ICreditCardPaymentTypeModel> {
        return this.internalPaymentProcessorService.deleteCreditCardPaymentType(creditCardPaymentTypeModel);
    }

    async deleteACHPaymentType(achPaymentTypeModel: IACHPaymentTypeModel): Promise<IACHPaymentTypeModel> {
        return this.internalACHService.deleteACHPaymentType(achPaymentTypeModel);
    }

    async processPrePayment(processCreditCardPaymentModel: ICreditCardProcessPaymentModel): Promise<ICreditCardProcessPaymentResult> {
        return this.internalPaymentProcessorService.processPrePayment(processCreditCardPaymentModel);
    }

    async processPayment(processCreditCardPaymentModel: ICreditCardProcessPaymentModel): Promise<ICreditCardProcessPaymentResult> {
        return this.internalPaymentProcessorService.processPayment(processCreditCardPaymentModel);
    }

    async voidPayment(paymentModel: IPaymentModel): Promise<IPaymentModel> {
        if (paymentModel?.paymentType === 'Terminal Payment')
            return this.northSIService.voidPayment(paymentModel);
        return this.internalPaymentProcessorService.voidPayment(paymentModel);
    }

    async refundPayment(paymentModel: IPaymentModel, amount:number): Promise<IPaymentModel> {
        if (paymentModel?.paymentType === 'Terminal Payment')
            return this.northSIService.refundPayment(paymentModel, amount);
        return this.internalPaymentProcessorService.refundPayment(paymentModel, amount);
    }

    async processACHPayment(processACHPaymentModel: IACHProcessPaymentModel): Promise<IACHProcessPaymentResultModel> {
        return this.internalACHService.processACHPayment(processACHPaymentModel);
    }

}